import { forwardRef } from 'react';
import { Cpu, Layers, ShieldCheck } from 'lucide-react';
import { SectionAtmosphere, TYPE_SECTION_EYEBROW, TYPE_LABEL_CAPS } from '../layout/HoldingBackdrop';

const About = forwardRef<HTMLElement>((_, ref) => {
  return (
    <section ref={ref} id="about" className="relative isolate overflow-hidden px-5 py-24 md:px-8 md:py-32">
      <SectionAtmosphere variant="about" />
      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <p className={TYPE_SECTION_EYEBROW}>Who we are</p>
          <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-[#f1f5ec]/90 md:text-5xl">
            Operating software for the energy transition
          </h2>
          <p className="mt-6 font-sans text-base leading-8 text-[#d6ddcf]/85 md:text-lg">
            Fidus Finance is a Brazilian technology company headquartered in Florianópolis, Brazil.
            We design and run the internal systems energy operators rely on — plant telemetry,
            performance monitoring and dispatch — and anchor those records with blockchain
            verification.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-3 md:gap-5">
          <div className="rounded-3xl border border-[#e8ede4]/10 bg-white/[0.035] p-8 transition hover:border-teal-200/35 hover:bg-white/[0.055]">
            <Cpu className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
            <p className={TYPE_LABEL_CAPS}>Operating platforms</p>
            <h3 className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">
              Custom systems, not generic SaaS
            </h3>
            <p className="mt-4 leading-7 text-[#d6ddcf]/85">
              Bespoke software for renewable operators — plant performance, EMS / SCADA integration,
              plant dispatch and O&amp;M workflows — built to a single operator’s reality.
            </p>
          </div>
          <div className="rounded-3xl border border-[#e8ede4]/10 bg-white/[0.035] p-8 transition hover:border-teal-200/35 hover:bg-white/[0.055]">
            <ShieldCheck className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
            <p className={TYPE_LABEL_CAPS}>Verifiable trust</p>
            <h3 className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">
              Blockchain‑anchored records
            </h3>
            <p className="mt-4 leading-7 text-[#d6ddcf]/85">
              Tamper-evident records for generation data — proofs operators and auditors can check
              independently.
            </p>
          </div>
          <div className="rounded-3xl border border-[#e8ede4]/10 bg-white/[0.035] p-8 transition hover:border-teal-200/35 hover:bg-white/[0.055]">
            <Layers className="mb-4 h-10 w-10 text-teal-200/85" aria-hidden />
            <p className={TYPE_LABEL_CAPS}>Data platform</p>
            <h3 className="mt-4 text-2xl font-semibold tracking-[-0.03em] text-[#f1f5ec]/88">
              One trail for plant data
            </h3>
            <p className="mt-4 leading-7 text-[#d6ddcf]/85">
              Software that brings telemetry, dispatch events and field records into a single audit
              trail for the operator.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
});

About.displayName = 'About';
export default About;
